<template>
  <!-- The full-height hero shared by the homepage and the landings (docs/blocks-redesign.md §4.2): media, the video's
       sound and play buttons, the link and form-link CTAs and the scroll indicator. The heading comes from the page. -->
  <div class="hp-hero">
    <div
      v-if="firstVideo"
      class="hp-hero__video-btn">
      <button
        class="cta"
        @click="muted = !muted">
        <span>{{ !muted ? $t("general.soundOn") : $t("general.soundOff") }}</span>
        <icon-sound-off v-if="muted" />
        <icon-sound-on v-if="!muted" />
      </button>

      <button
        class="cta"
        @click="paused = !paused">
        <span>{{ paused ? $t("general.playVideo") : $t("general.pauseVideo") }}</span>
        <icon-pause v-if="!paused" />
        <icon-play v-if="paused" />
      </button>
    </div>

    <div class="hp-hero__media">
      <base-responsive-media
        role="presentation"
        :paused="paused"
        :autoplay="true"
        :muted="muted"
        homepage
        :media="media" />
    </div>

    <div class="hp-hero__content">
      <div class="hp-hero__lead">
        <slot />
        <div
          v-if="linkAttrs(link)"
          class="hp-hero__cta">
          <cta-veneta-icon
            color="white"
            :text="link.label ?? $t('actions.discover')"
            v-bind="linkAttrs(link)">
            {{ link.label ?? $t("actions.discover") }}
          </cta-veneta-icon>
        </div>
        <div
          v-if="exists(formLink?.form)"
          class="hp-hero__cta">
          <cta-veneta-icon
            color="white"
            :text="formLink?.label ?? $t('actions.discover')"
            @click="modalOpen = true">
            download catalog
          </cta-veneta-icon>
        </div>
      </div>

      <slot name="aside" />
    </div>
    <scroll-indicator :label="false" />

    <teleport to="body">
      <base-modal
        v-if="exists(formLink?.form)"
        :show="modalOpen"
        close-inside
        class="hp-hero__modal"
        @close="modalOpen = false">
        <template #default>
          <div class="hp-hero__modal-wr">
            <div class="hp-hero__modal-title">
              {{ formLink.label }}
            </div>
            <form-builder
              :id="`form-hero-${formLink.form?.handle}`"
              cta-color="red"
              :wrapped="false"
              :handle="formLink.form?.handle" />
          </div>
        </template>
      </base-modal>
    </teleport>
  </div>
</template>

<script setup>
  import IconPause from '~/assets/svgs/icon/white/pause.svg'
  import IconPlay from '~/assets/svgs/icon/white/play.svg'
  import IconSoundOn from '~/assets/svgs/icon/white/speaker-high.svg'
  import IconSoundOff from '~/assets/svgs/icon/white/speaker-none.svg'

  const layoutStore = useLayoutStore()
  const { bus } = useNuxtApp()

  const props = defineProps({
    link: Object,
    formLink: Object,
    imageDesktop: Array,
    imageMobile: Array,
    video: Array
  })

  const paused = ref(false)
  const muted = ref(true)
  const modalOpen = ref(false)

  const firstVideo = computed(() => exists(props.video) ? getFirst(props.video) : null)

  const media = computed(() => {
    const video = firstVideo.value

    return {
      poster: video ? getFirst(video.poster) : null,
      videoUrl: video ? (getFirst(video.videoSourceDesktop)?.src || video.videoUrl?.url || null) : null,
      mobileVideoUrl: video ? (getFirst(video.videoSourceMobile)?.src || video.mobileVideoUrl?.url || null) : null,
      image: getFirst(props.imageDesktop),
      mobileImage: getFirst(props.imageMobile)
    }
  })

  // LCP optimization: the hero poster is the largest paintable element while
  // the video loads. L'ordine segue i <source> del <picture> reso da HpVideo
  // (avif per primo), con il type coerente così i browser che non lo
  // supportano ignorano il preload invece di scaricare il file sbagliato.
  useHead(() => {
    const poster = media.value.poster
    const href = poster ? poster.avif ?? poster.webp ?? poster.src ?? null : null
    if (!href) { return {} }
    const type = poster.avif ? 'image/avif' : poster.webp ? 'image/webp' : null

    return {
      link: [
        {
          rel: 'preload',
          as: 'image',
          href,
          fetchpriority: 'high',
          ...(type ? { type } : {})
        }
      ]
    }
  })

  onMounted(async () => {
    await nextTick()
    layoutStore.headerSpace = false
    bus?.emit?.('scroll:refresh')
  })
</script>

<style lang="scss" scoped>
  .hp-hero {
    width: 100%;
    position: relative;
    background-color: var(--color-black);
    display: grid;
    grid-template-columns: repeat(1, minmax(0, 1fr));
    grid-template-rows: minmax(0, var(--header-height)) minmax(0, auto);

    @include mq(medium) {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    &__media,
    &__content {
      grid-column: 1 / span 2;
      grid-row: 1 / span 2;
    }

    &__content {
      @include wrapper;

      position: relative;
      z-index: 2;
      grid-row: 2 / span 1;
      color: var(--color-white);
      display: flex;
      align-items: flex-end;
      padding-top: var(--header-height);
      padding-bottom: rem(40px);

      @include mq(desktop) {
        display: grid;
        gap: rem(24px);
        grid-template-columns: repeat(2, minmax(0, 1fr));
        grid-template-rows: minmax(0, 1fr) minmax(0, max-content);
      }
    }

    .scroll-indicator {
      display: none;
      position: absolute;
      bottom: 0;
      left: calc(50% - 2.5px);

      @include mq(medium) {
        display: block;
        bottom: rem(40px);
      }
    }

    &__lead {
      display: flex;
      flex-direction: column;
      gap: rem(24px);
      max-width: rem(440px);
      grid-row: 2 / span 1;
    }

    &__media {
      pointer-events: none;
      height: 90dvh;

      @include mq(medium) {
        // A fixed height prevents the grid row from following the video's
        // changing intrinsic ratio when the poster gives way to its first frame.
        height: calc(100dvh - var(--header-height));
      }
    }

    &__video-btn {
      position: relative;
      z-index: 3;
      grid-row: 2 / span 1;
      grid-column: 1 / span 2;
      display: flex;
      gap: rem(24px);
      align-items: flex-start;
      align-self: flex-start;
      justify-content: center;
      padding: rem(16px) 0;
    }

    &__video-btn .cta {
      color: var(--color-white);
      display: flex;
      align-items: center;
      gap: rem(4px);

      @include body-s;

      span {
        line-height: 0;
      }

      svg {
        width: rem(24px);
      }
    }

    &__modal {
      :deep(.base-modal__dialog) {
        background: var(--color-white);
        width: 70vw;

        .form-loader {
          padding: 0 0 40px;
        }
      }

      :deep(.base-modal__close) {
        right: 40px;
        position: absolute;
      }
    }

    &__modal-title {
      @include body-l;

      margin-bottom: 48px;
    }

    &__cta :deep(.cta.cta--solid) {
      background-color: rgb(255, 255, 255, 0.2);
      backdrop-filter: blur(20px) saturate(180%);
      will-change: backdrop-filter;

      // Keep the border in the SSR stylesheet instead of waiting for the
      // equally-specific non-scoped Cta.vue stylesheet during hydration.
      border: 1px solid var(--color-white);
    }

    :deep(video) {
      max-height: 100vh;
    }
  }
</style>
